import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type {
	InlineCommentSelectionComponentProps,
	InlineCommentViewComponentProps,
	InlineCommentHoverComponentProps,
} from '@atlaskit/editor-common/types';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { CommentEditor } from '@confluence/comment/entry-points/LazyCommentEditor.ts';
import { getRendererAnnotationStates } from '@confluence/comments-util/entry-points/stateResolver.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { HoverComponentProps } from './HoverComponent.tsx';
import type { ViewComponentProps } from './ViewComponent.tsx';
import { SelectionManager, type BaseSelectionComponentProps } from './SelectionManager.tsx';
import { ViewManager } from './ViewManager.tsx';
import { HoverManager } from './HoverManager.tsx';

type getRendererInlineCommentOptionsProps = {
	pageId: string;
	validAnnotationsCallback: (validAnnotations: string[]) => void;
	triggerAnalyticsEvent: any;
	setSelectionProps: (
		selectionProps: (InlineCommentSelectionComponentProps & BaseSelectionComponentProps) | null,
	) => void;
	setHoverProps: (
		hoverProps: (InlineCommentHoverComponentProps & HoverComponentProps) | null,
	) => void;
	setViewProps: (viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null) => void;
	updateDocument?: (doc: JSONDocNode) => void;
	isArchived?: boolean;
	allowCommentsOnMedia?: boolean;
	isHighlightOptionPanelEnabled: boolean;
};

export const getRendererInlineCommentOptions = ({
	pageId,
	validAnnotationsCallback,
	triggerAnalyticsEvent,
	setSelectionProps,
	setViewProps,
	updateDocument,
	isArchived,
	setHoverProps,
	isHighlightOptionPanelEnabled,
}: getRendererInlineCommentOptionsProps) => {
	let updateSubscriber = getRendererAnnotationEventEmitter();
	if (!updateSubscriber?.off) {
		updateSubscriber = undefined;
	}

	return {
		selectionComponent:
			isHighlightOptionPanelEnabled ||
			expValEquals('confluence_frontend_allow_comments_if_toolbar_off', 'isEnabled', true)
				? SelectionManager({
						pageId,
						updateDocument,
						isArchived,
						setSelectionProps,
						onCommentButtonMount: () => CommentEditor.preload(),
					})
				: undefined,
		viewComponent: ViewManager({
			pageId,
			updateDocument,
			isArchived,
			setViewProps,
		}),
		hoverComponent: HoverManager({
			pageId,
			updateDocument,
			isArchived,
			setHoverProps,
			onCommentButtonMount: () => CommentEditor.preload(),
		}),
		getState: (annotations: string[], isNestedRender: boolean) =>
			getRendererAnnotationStates({
				annotations,
				pageId,
				validAnnotationsCallback,
				triggerAnalyticsEvent,
				isNestedRender,
			}),
		updateSubscriber,
		disallowOnWhitespace: true,
		isToolbarAbove: true,
		allowDraftMode: true,
		allowCommentsOnMedia: true,
	};
};
